import axios from "axios";
import type { ICampaign, ICampaignAccountMoveResult } from "../Types/account";
import type { ICreateCampaignRequest } from "../Types/campaignCreate";
import type { GuidanceObject } from "../Types/writingGuidance";
import {
  TUTORIAL_AMC_CLIENT_ID,
  TUTORIAL_DEFAULT_CAMPAIGN_ID,
} from "../Constants/tutorialScenario";
import { isTutorialFixtureActive } from "../utils/tutorialFixtureState";
import {
  buildCampaignCreatePayload,
  buildFileMetadata,
} from "../utils/createCampaignHelpers";
import { getUserFromLS } from "../utils/getUserFromLS";

function authorizationHeaders() {
  const user = getUserFromLS();
  return { Authorization: `Bearer ${user.access_token}` };
}

export async function getClientCampaigns(
  clientId: string,
): Promise<ICampaign[]> {
  if (isTutorialFixtureActive() && clientId === TUTORIAL_AMC_CLIENT_ID) {
    return [
      {
        id: TUTORIAL_DEFAULT_CAMPAIGN_ID,
        client_id: clientId,
        name: "Default",
        is_default: true,
        created_at: "",
        updated_at: "",
      },
    ];
  }

  const response = await axios.get<ICampaign[]>(
    `${import.meta.env.VITE_API_BASE_URL}/clients/${clientId}/campaigns`,
    { headers: authorizationHeaders() },
  );
  return response.data;
}

export type CampaignDescription = ICampaign & {
  campaign_description: string | null;
};

export type CampaignWritingGuidance = CampaignDescription & {
  // Keep the boundary unknown so malformed stored data can be repaired explicitly.
  writing_guidance: unknown;
};

export async function getCampaignWritingGuidance(
  campaignId: string,
  signal?: AbortSignal,
): Promise<CampaignWritingGuidance> {
  const response = await axios.get<CampaignWritingGuidance>(
    `${import.meta.env.VITE_API_BASE_URL}/campaigns/${encodeURIComponent(campaignId)}`,
    { headers: authorizationHeaders(), params: { include_config: true }, signal },
  );
  return response.data;
}

export async function saveCampaignWritingGuidance(
  campaignId: string,
  guidance: GuidanceObject,
  signal?: AbortSignal,
): Promise<void> {
  await axios.patch(
    `${import.meta.env.VITE_API_BASE_URL}/campaigns/${encodeURIComponent(campaignId)}`,
    { writing_guidance: guidance },
    { headers: authorizationHeaders(), signal },
  );
}

export async function getCampaignDescription(
  campaignId: string,
  signal?: AbortSignal,
): Promise<CampaignDescription> {
  const response = await axios.get<CampaignDescription>(
    `${import.meta.env.VITE_API_BASE_URL}/campaigns/${encodeURIComponent(campaignId)}`,
    { headers: authorizationHeaders(), signal },
  );
  return response.data;
}

export async function saveCampaignDescription(
  campaignId: string,
  description: string,
): Promise<CampaignDescription> {
  const response = await axios.patch<CampaignDescription>(
    `${import.meta.env.VITE_API_BASE_URL}/campaigns/${encodeURIComponent(campaignId)}`,
    { campaign_description: description.trim() },
    { headers: authorizationHeaders() },
  );
  return response.data;
}

export async function createCampaign(
  clientId: string,
  request: ICreateCampaignRequest,
): Promise<ICampaign> {
  const URL = `${import.meta.env.VITE_API_BASE_URL}/clients/${clientId}/campaigns`;

  const formData = new FormData();
  formData.append(
    "campaign",
    JSON.stringify(buildCampaignCreatePayload(clientId, request)),
  );

  const metadata = buildFileMetadata(request.files);
  request.files.forEach((entry) => {
    formData.append("files", entry.file);
  });
  formData.append("file_metadata", JSON.stringify(metadata));

  const response = await axios.post<ICampaign>(URL, formData, {
    headers: authorizationHeaders(),
  });
  return response.data;
}

export async function bulkAssignAccountsToCampaign(
  campaignId: string,
  accountIds: Array<string | number>,
): Promise<ICampaignAccountMoveResult> {
  const response = await axios.post<ICampaignAccountMoveResult>(
    `${import.meta.env.VITE_API_BASE_URL}/campaigns/${campaignId}/accounts/bulk-assign`,
    { account_ids: accountIds.map(String) },
    { headers: authorizationHeaders() },
  );
  return response.data;
}

export async function bulkAssignAccountsToCampaignChunked(
  campaignId: string,
  accountIds: Array<string | number>,
): Promise<ICampaignAccountMoveResult> {
  const result: ICampaignAccountMoveResult = { succeeded: [], failed: [] };
  for (let index = 0; index < accountIds.length; index += 50) {
    const chunk = accountIds.slice(index, index + 50);
    const chunkResult = await bulkAssignAccountsToCampaign(campaignId, chunk);
    result.succeeded.push(...chunkResult.succeeded);
    result.failed.push(...chunkResult.failed);
  }
  return result;
}
